import httpClient from '../../../api/httpClient';
import API_CONFIG from '../../../config/api.config';
import { 
  Appointment, 
  AppointmentDto,
  AppointmentFormData, 
  GetAppointmentsQueryParams, 
  PaginatedResponse,
  AppointmentStatus
} from '../types/appointment.types';

const ENDPOINT = API_CONFIG.ENDPOINTS.APPOINTMENTS;

/**
 * Helper function to convert DTO to frontend model
 */
const mapAppointmentDtoToModel = (dto: AppointmentDto): Appointment => {
  console.log('=== RAW DTO RECEIVED ===');
  console.log('Complete DTO:', JSON.parse(JSON.stringify(dto)));
  
  const startDate = new Date(dto.startTime);
  
  // Log all available patient data
  console.log('=== PATIENT DATA ===');
  console.log('Patient ID:', dto.patientId);
  console.log('Patient Object:', dto.patient);
  console.log('Patient Full Name:', dto.patient?.fullName);
  
  // Get patient name from fullName field or use patient ID
  const patientName = dto.patient?.fullName?.trim() || 
                     (dto.patientId ? `Patient ${dto.patientId.substring(0, 6)}` : 'Walk-in');
  
  // Log all available doctor data
  console.log('=== DOCTOR DATA ===');
  console.log('Doctor ID:', dto.doctorId);
  console.log('Doctor Object:', dto.doctor);
  
  // Get doctor name from first/last name or designation
  const doctorName = dto.doctor 
    ? [dto.doctor.firstName, dto.doctor.lastName].filter(Boolean).join(' ').trim() || 
      dto.doctor.designation || 
      `Doctor ${dto.doctor.employeeId || dto.doctorId?.substring(0, 6) || 'Unassigned'}`
    : 'Unassigned';
  
  // Format dates and times
  const formattedDate = startDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  
  const formattedTime = startDate.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
  
  // Generate a readable appointment number if none exists
  const apptNumber = dto.appointmentNumber || 
                    `APP-${startDate.getFullYear()}${String(startDate.getMonth() + 1).padStart(2, '0')}${String(dto.id).substring(0, 4).toUpperCase()}`;
  
  // Ensure patient name is properly formatted
  const finalPatientName = patientName || 
                         (dto.patient?.fullName || 
                          (dto.patientId ? `Patient (${dto.patientId.substring(0, 6)})` : 'Walk-in'));
  
  // Ensure doctor name is properly formatted
  const finalDoctorName = doctorName || 
                        (dto.doctorId ? `Doctor (${dto.doctorId.substring(0, 6)})` : 'Unassigned');
  
  const mapped: Appointment = {
    ...dto,
    appointmentNumber: apptNumber,
    appointmentDate: formattedDate,
    date: formattedDate,
    time: formattedTime,
    patientName: finalPatientName,
    doctorName: finalDoctorName,
    // Ensure these are included in the mapped object
    startTime: dto.startTime,
    endTime: dto.endTime,
    status: dto.status,
    type: dto.type,
    priority: dto.priority || 'MEDIUM' as const
  };
  
  console.log('=== MAPPED APPOINTMENT ===');
  console.log('Mapped Object:', JSON.parse(JSON.stringify(mapped)));
  console.log('Appointment Number:', mapped.appointmentNumber);
  console.log('Patient Name:', mapped.patientName);
  console.log('Doctor Name:', mapped.doctorName);
  console.log('Date:', mapped.date);
  console.log('Time:', mapped.time);
  console.log('==========================');
  
  return mapped;
};

/**
 * Helper function to convert form data to DTO
 */
const mapFormDataToDto = (data: AppointmentFormData): Partial<AppointmentDto> => {
  const startTime = new Date(`${data.date}T${data.time}`);
  const endTime = new Date(startTime.getTime() + data.duration * 60000);
  
  return {
    patientId: data.patientId,
    doctorId: data.doctorId,
    locationId: data.locationId,
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    type: data.type,
    status: data.status,
    reason: data.reason,
    notes: data.notes,
    isWalkIn: data.isWalkIn,
    priority: data.priority,
  };
};

export const appointmentApi = {
  /**
   * Get paginated list of appointments with optional filters
   */
  getAll: async (params?: GetAppointmentsQueryParams): Promise<PaginatedResponse<Appointment>> => {
    console.log('=== API REQUEST PARAMS ===', params);
    
    // Convert array parameters to the format expected by the backend
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Handle array parameters
          value.forEach(v => {
            if (v !== undefined && v !== null) {
              queryParams.append(key, v.toString());
            }
          });
        } else if (value !== undefined && value !== null) {
          // Handle single value parameters
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `${ENDPOINT}?${queryParams.toString()}`;
    console.log('Sending GET request to:', url);
    
    try {
      console.log('Request headers:', {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')?.substring(0, 20)}...`
      });
      
      const response = await httpClient.get<PaginatedResponse<AppointmentDto>>(url);
      
      console.log('=== API RESPONSE ===', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
        count: response.data?.data?.length || 0,
        meta: response.data?.meta,
        firstItem: response.data?.data?.[0] || 'No items'
      });
      
      const mappedData = {
        ...response.data,
        data: response.data.data.map(mapAppointmentDtoToModel)
      };
      
      console.log('Mapped appointments data:', {
        count: mappedData.data.length,
        firstItem: mappedData.data[0] || 'No items',
        meta: mappedData.meta
      });
      
      return mappedData;
    } catch (error) {
      console.error('Error fetching appointments:', {
        error,
        endpoint: ENDPOINT,
        params: params || {}
      });
      throw error;
    }
  },

  /**
   * Get a single appointment by ID
   */
  getById: async (id: string): Promise<Appointment> => {
    const response = await httpClient.get<AppointmentDto>(`${ENDPOINT}/${id}`);
    return mapAppointmentDtoToModel(response.data);
  },

  /**
   * Create a new appointment
   */
  create: async (data: AppointmentFormData): Promise<Appointment> => {
    const dto = mapFormDataToDto(data);
    const response = await httpClient.post<AppointmentDto>(ENDPOINT, dto);
    return mapAppointmentDtoToModel(response.data);
  },

  /**
   * Update an existing appointment
   */
  update: async (id: string, data: AppointmentFormData): Promise<Appointment> => {
    const dto = mapFormDataToDto(data);
    const response = await httpClient.patch<AppointmentDto>(`${ENDPOINT}/${id}`, dto);
    return mapAppointmentDtoToModel(response.data);
  },

  /**
   * Delete an appointment
   */
  delete: async (id: string): Promise<void> => {
    await httpClient.delete(`${ENDPOINT}/${id}`);
  },
  
  /**
   * Update appointment status
   */
  updateStatus: async (id: string, status: AppointmentStatus, notes?: string): Promise<Appointment> => {
    const response = await httpClient.patch<AppointmentDto>(
      `${ENDPOINT}/${id}/status`,
      { status, notes }
    );
    return mapAppointmentDtoToModel(response.data);
  },

  /**
   * Update appointment doctor
   */
  updateDoctor: async (id: string, doctorId: string): Promise<Appointment> => {
    const response = await httpClient.patch<AppointmentDto>(
      `${ENDPOINT}/${id}/doctor`,
      { doctorId }
    );
    return mapAppointmentDtoToModel(response.data);
  },
  
  /**
   * Reschedule appointment
   */
  reschedule: async (id: string, data: {
    appointmentDate: string;
    startTime: string;
    endTime: string;
    locationId: string;
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
    type: 'CONSULTATION' | 'FOLLOW_UP' | 'THERAPIES' | 'OTC' | 'WALK_IN' | 'OTHER';
    notes?: string;
    reason?: string;
  }): Promise<Appointment> => {
    const response = await httpClient.patch<AppointmentDto>(`${ENDPOINT}/${id}/reschedule`, data);
    return mapAppointmentDtoToModel(response.data);
  },

  /**
   * Check appointment availability
   */
  checkAvailability: async (params: {
    doctorId: string;
    date: string;
    duration: number;
    excludeAppointmentId?: string;
  }): Promise<{ available: boolean; suggestedSlots: string[] }> => {
    const response = await httpClient.get(`${ENDPOINT}/check-availability`, { params });
    return response.data;
  },

  /**
   * Get available time slots for a doctor on a specific date
   */
  getAvailableSlots: async (doctorId: string, date: string, duration: number): Promise<Date[]> => {
    const response = await httpClient.get<{ slots: string[] }>(
      `${ENDPOINT}/slots/available`,
      { params: { doctorId, date, duration } }
    );
    return response.data.slots.map(slot => new Date(slot));
  }
};
